import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { HashRouter, Redirect, Route, RouteComponentProps } from 'react-router-dom';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { LoginUrls } from '@tableau/shared-urls';

import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { AuthLevel, ServerService } from '../../../ts/services/ServerService';
import { SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { RouterError } from '../RouterHelpers/RouterError';
import { TableauReactRouterSwitch } from '../RouterHelpers/TableauReactRouterSwitch';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { LoginErrorProvider } from './LoginErrorProvider';
import { LoginUrlsWrapper } from './LoginUrls';
import { RequestPasswordResetProvider } from './RequestPasswordResetProvider';
import { ResetPasswordProvider, ResetPasswordProviderProps } from './ResetPasswordProvider';
import { SignedOutProvider, SiteSamlSignedOutProvider } from './SignedOutProviders';
import { SignInProvider, SignInProviderProps } from './SignInProvider';
import { SitePickerProvider } from './SitePickerProvider';

export interface ResetPasswordRouteParams {
  readonly token: string;
}
export interface ResetPasswordRouteComponentProps extends RouteComponentProps<ResetPasswordRouteParams> {}

export interface LoginRouterOwnProps extends ReactDirectiveProps {}

export interface LoginRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isSessionMultiSite: boolean;
}

export type LoginRouterProps = LoginRouterOwnProps & LoginRouterStateProps;

export class LoginRouterComponent extends React.Component<LoginRouterProps> {
  private server: ServerService;

  public constructor(props: LoginRouterProps) {
    super(props);
    this.server = ServiceLocator.get<ServerService>('ServerService');
  }

  private memoizedGetWatchedBasePaths = defaultMemoize(LoginRouterComponent.getWatchedBasePaths);
  public render() {
    const basePaths = this.memoizedGetWatchedBasePaths();
    return (
      <TableauReactRouterSwitch basePaths={basePaths} urls={this.props.urls}>
        <Route path={LoginUrlsWrapper.getInstance().signinPath()} render={this.signinProvider()} />
        {this.protectedRouteForSitePicker(this.server, this.props.angularContext)}
        <Route path={LoginUrlsWrapper.getInstance().loginErrorPath()} render={() => <LoginErrorProvider {...this.props}/>} />
        <Route path={LoginUrlsWrapper.getInstance().signedOutPath()} render={() => <SignedOutProvider {...this.props}/>} />
        <Route path={LoginUrlsWrapper.getInstance().siteSamlSignedOutPath()} render={() => <SiteSamlSignedOutProvider {...this.props} />} />
        <Route exact path={LoginUrlsWrapper.getInstance().passwordResetWithTokenPath(':token')} render={this.resetPasswordProvider()} />
        <Route exact path={LoginUrlsWrapper.getInstance().passwordResetUserNameBasePath()} render={this.resetPasswordProvider()} />
        <Route exact path={LoginUrlsWrapper.getInstance().requestPasswordResetPath()} render={() => <RequestPasswordResetProvider {...this.props}/>} />
        <Route render={this.error}/>
      </TableauReactRouterSwitch>
    );
  }

  private resetPasswordProvider = (): (props: ResetPasswordRouteComponentProps) => JSX.Element => {
    return (props: ResetPasswordRouteComponentProps): JSX.Element => {
      const currentQueryValues = queryString.parse(props.location.search);
      const username = currentQueryValues[LoginUrls.usernameQueryKey()];
      const resetPasswordProviderProps: ResetPasswordProviderProps = {
        angularContext: this.props.angularContext,
        token: props.match.params.token,
        username
      };
      return <ResetPasswordProvider {...resetPasswordProviderProps} />;
    };
  }

  private signinProvider = (): (props: RouteComponentProps) => JSX.Element => {
    return (props: RouteComponentProps): JSX.Element => {
      const signinProps: SignInProviderProps = {
        routeLocationSearch: props.location.search,
        angularContext: this.props.angularContext,
      };

      return <SignInProvider {...signinProps}/>;
    };
  }

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }

  private protectedRouteForSitePicker(server: ServerService, angularContext: AngularContext) {
    return <Route exact={true} path={LoginUrlsWrapper.getInstance().sitePath()} render={() => {
      if (server.isUserAuthorized(AuthLevel.Authenticated)) {
        return <SitePickerProvider angularContext={angularContext}/>;
      } else {
        return <Redirect to={LoginUrlsWrapper.getInstance().signinPath()} />;
      }
    }}/>;
  }

  public static getWatchedBasePaths() {
    return _.compact([
      LoginUrlsWrapper.getInstance().signinPath(),
      LoginUrlsWrapper.getInstance().sitePath(),
      LoginUrlsWrapper.getInstance().loginErrorPath(),
      LoginUrlsWrapper.getInstance().passwordResetUserNameBasePath(),
      LoginUrlsWrapper.getInstance().requestPasswordResetPath()
    ]);
  }
}



const mapStateToProps: MapStateToProps<LoginRouterStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state)
});

export const ConnectedLoginRouterComponent: React.ComponentClass<LoginRouterOwnProps>
  = connect(mapStateToProps, null, null, {pure: false})(LoginRouterComponent);

export function LoginRouter(props: LoginRouterOwnProps){
  return (
    <HashRouter>
      <ConnectedLoginRouterComponent {...props} />
    </HashRouter>
  );
}
